/**
 * VendorContext - Provides vendor organization context for the Vendor Portal
 *
 * Exposes whether the current org is a vendor, their client orgs, and selection state.
 * Only fetches vendor data when the user's org is marked as a vendor.
 */
import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { VendorAccessService, VendorPortalService } from "api/vendorPortal.api";
import PortalAssetsApiService from "afms/api/portalAssets.api";

const VendorContext = createContext(null);

export const VendorProvider = ({ children }) => {
  const user = useSelector((state) => state.auth.user);
  const [clientOrgs, setClientOrgs] = useState([]);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [selectedClientOrgId, setSelectedClientOrgId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Caller's resolved afms PortalRole + permitted actions — used to gate the
  // vendor-portal sidebar against the same permissions the API enforces.
  // null while unknown (sidebar then degrades to showing every item).
  const [portalCapabilities, setPortalCapabilities] = useState(null);

  // Vendor identity is purely org-level after CollapsePrimaryRoles
  // (2026-06-11). The role-string legs (VENDOR / RECYCLING_VENDOR /
  // MAINTENANCE_VENDOR) were removed because no user holds those
  // primary roles anymore.
  const isVendorOrg = useMemo(() => {
    return user?.userDetails?.isVendorOrganization === true;
  }, [user?.userDetails]);

  const vendorRole = useMemo(() => {
    return user?.userDetails?.role?.toUpperCase() || null;
  }, [user?.userDetails?.role]);

  // Also load the customer list for an org that serves customers without
  // the vendor flag (Command Center turned on; Jam 0c74b41e). isVendorOrg
  // itself stays the flag, since pages use it to hide client-side tools.
  const servesCustomers = user?.userDetails?.servesCustomers === true;

  // Fetch vendor data when org is a vendor or serves customers
  useEffect(() => {
    if ((!isVendorOrg && !servesCustomers) || !user?.userDetails?.organizationId) return;

    const fetchVendorData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [clientsRes, profileRes] = await Promise.allSettled([
          VendorAccessService.listClients(),
          VendorAccessService.getVendorProfile(),
        ]);

        if (clientsRes.status === "fulfilled" && clientsRes.value?.data?.data) {
          setClientOrgs(clientsRes.value.data.data);
        }
        if (profileRes.status === "fulfilled" && profileRes.value?.data?.data) {
          setVendorProfile(profileRes.value.data.data);
        }

        const allFailed =
          clientsRes.status === "rejected" && profileRes.status === "rejected";
        const anyFailed =
          clientsRes.status === "rejected" || profileRes.status === "rejected";

        if (allFailed) {
          const firstErr = clientsRes.reason || profileRes.reason;
          setError({
            message: "Failed to load vendor data",
            isForbidden: firstErr?.response?.status === 403,
          });
        } else if (anyFailed) {
          setError({
            message: "Some vendor data could not be loaded",
            partial: true,
            isForbidden: false,
          });
        }
      } catch (err) {
        console.error("Failed to load vendor context:", err);
        setError({ message: "Failed to load vendor data", isForbidden: false });
      } finally {
        setLoading(false);
      }
    };

    fetchVendorData();
  }, [isVendorOrg, servesCustomers, user?.userDetails?.organizationId]);

  // Resolve the caller's afms portal capabilities for ANY logged-in portal
  // user (dealer or vendor) — the sidebar gate needs it regardless of org
  // type. Silent on failure: the sidebar then shows all items.
  useEffect(() => {
    if (!user?.userDetails?.organizationId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await PortalAssetsApiService.getMyCapabilities();
        const caps = res?.data?.data;
        if (!cancelled && caps) setPortalCapabilities(caps);
      } catch (err) {
        if (!cancelled) {
          console.warn("Failed to load portal capabilities:", err);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.userDetails?.organizationId]);

  const clearError = useCallback(() => setError(null), []);

  const refreshClients = useCallback(async () => {
    try {
      const res = await VendorAccessService.listClients();
      if (res?.data?.data) {
        setClientOrgs(res.data.data);
        setError(null);
      }
    } catch (err) {
      console.error("Failed to refresh vendor clients:", err);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const res = await VendorAccessService.getVendorProfile();
      if (res?.data?.data) {
        setVendorProfile(res.data.data);
        setError(null);
      }
    } catch (err) {
      console.error("Failed to refresh vendor profile:", err);
    }
  }, []);

  const value = useMemo(
    () => ({
      isVendorOrg,
      vendorRole,
      clientOrgs,
      vendorProfile,
      portalCapabilities,
      selectedClientOrgId,
      setSelectedClientOrgId,
      loading,
      error,
      clearError,
      refreshClients,
      refreshProfile,
      activeClients: clientOrgs.filter((c) => c.status === 1),
      pendingInvitations: clientOrgs.filter((c) => c.status === 0),
    }),
    [isVendorOrg, vendorRole, clientOrgs, vendorProfile, portalCapabilities, selectedClientOrgId, loading, error, clearError, refreshClients, refreshProfile]
  );

  return <VendorContext.Provider value={value}>{children}</VendorContext.Provider>;
};

/**
 * useVendor - Hook to access vendor context
 *
 * Usage:
 *   const { isVendorOrg, clientOrgs, selectedClientOrgId } = useVendor();
 */
export const useVendor = () => {
  const context = useContext(VendorContext);
  if (!context) {
    return {
      isVendorOrg: false,
      vendorRole: null,
      clientOrgs: [],
      vendorProfile: null,
      portalCapabilities: null,
      selectedClientOrgId: null,
      setSelectedClientOrgId: () => {},
      loading: false,
      error: null,
      clearError: () => {},
      refreshClients: () => {},
      refreshProfile: () => {},
      activeClients: [],
      pendingInvitations: [],
    };
  }
  return context;
};

export default VendorContext;
